import { Grid } from "@material-ui/core";
import { AddCircleOutline } from "@material-ui/icons";
import { useEffect, useState, useRef } from "react";
import { Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { submitAddPhaseRequest } from "../../../redux/actions/lenderRequestActions";
import PhasePanel from "./PhasePanel";
import StreetAddressModal from "./StreetAddressModal";
import "../../AddNewProject/AddNewProject.css";

const RequestNewPhasePage = ({ incStepCounter, projectInfo, formState }) => {
  const [phases, setPhases] = useState([]);
  const [phasesValid, setPhasesValid] = useState([]);
  const [comment, setComment] = useState("");
  const [commentLength, setCommentLength] = useState(0);
  const [forceValidation, setForceValidation] = useState(false);
  const [allValid, setAllValid] = useState(false);

  // Street address modal state
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [activePhaseIndex, setActivePhaseIndex] = useState(null);

  const dispatch = useDispatch();

  useEffect(() => {
    if (projectInfo && !phases.length) {
      // console.log("Initializing phases with projectInfo", projectInfo);
      handlePushNewPhase();
    }
  }, [projectInfo, phases.length]);
  
  // Load form state (if returning from later step) and pre-populate fields
  useEffect(() => {
    if (formState) {
      // console.log("Loading form state into RequestNewPhasePage", formState);

      if (formState.phases) {
        setPhases(formState.phases);
        setPhasesValid(formState.phases.map(() => true)); // Assume previously saved phases are valid
        setForceValidation(true);
      }

      if (formState.comment) {
        setComment(formState.comment);
        setCommentLength(formState.comment.length);
        setForceValidation(true);
      }
    }
  }, [formState]);

  useEffect(() => {
    setAllValid(phasesValid.every(Boolean));
  }, [phasesValid]);

  // Handle opening the street address modal
  const handleStreetInputFocus = (phaseIndex) => {
    console.log(`Opening address modal for phase index ${phaseIndex}`, phases[phaseIndex]);
    setActivePhaseIndex(phaseIndex);
    setShowAddressModal(true);
  };

  // Handle address selection from modal
  const handleAddressSelect = (streetAddress, phaseIndex) => {
    if (streetAddress && phaseIndex !== null) {
      const updatedPhases = [...phases];
      updatedPhases[phaseIndex] = {
        ...updatedPhases[phaseIndex],
        street: streetAddress
      };
      setPhases(updatedPhases);

      // The PhasePanel will receive the updated phase prop and re-validate itself
      // No need to manually update validation here since PhasePanel handles its own validation
    }
  };

  // Handle closing the modal
  const handleCloseAddressModal = () => {
    // console.log("Closing address modal");
    setShowAddressModal(false);
    setActivePhaseIndex(null);
  };

  function createNewPhase() {
    return {
      phaseName: "",
      phaseType: "Legal",
      buildingNumber: "",
      unitNumber: "",
      unitsPerPhase: "",
      street: projectInfo?.street || "",
      city: projectInfo?.city || "",
      state: projectInfo?.state || "",
      zip5: projectInfo?.zip5 || "",
    };
  }

  function handlePushNewPhase() {
    // console.log("Adding new phase", {phases, phasesValid});
    setPhases([...phases, createNewPhase()]);
    setPhasesValid([...phasesValid, false]); // New phase starts as invalid
  }

  async function handleFormSubmit(e) {
    e.preventDefault();
    setForceValidation(true);

    if (allValid && phases.length) {
      async function requestCallback() {
        const requestId = await dispatch(submitAddPhaseRequest(phases, comment));
        return {requestId};
      }

      const formStateData = {phases, comment, phasesValid};
      incStepCounter(formStateData, requestCallback);
    }
  }

  return (
    <Grid item xs={12}>
      <form id="addProjectForm" onSubmit={handleFormSubmit}>
        <Grid item xs={12}>
          <h1 style={{ justifyContent: "center" }}>
            Request New Phase
          </h1>
        </Grid>
        <Grid item xs={12} styles={{ marginTop: 20 }}>
          <h6 style={{ justifyContent: "center" }}>
            Step 1 of 3 - New Project Details
          </h6>
          <p>You can request addition of one or more legal phases.</p>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} sm ={12} md={11}>
            <div className="phase-list">
              {phases.map((phase, index) => (
                <PhasePanel
                  key={index}
                  phase={phase}
                  phaseIndex={index}
                  canDelete={!!index}
                  forceValidation={forceValidation}
                  onStreetInputFocus={() => handleStreetInputFocus(index)}
                  onChange={(formData, isValid) => {
                    // Update the phase in the local state using immutable update
                    const updatedPhases = [...phases];
                    updatedPhases[index] = formData;
                    setPhases(updatedPhases);
                    
                    // Update validation state using immutable update
                    const updatedPhasesValid = [...phasesValid];
                    updatedPhasesValid[index] = isValid;
                    setPhasesValid(updatedPhasesValid);
                  }}
                  onDelete={() => {
                    const updatedPhases = phases.filter((_, i) => i !== index);
                    const updatedPhasesValid = phasesValid.filter((_, i) => i !== index);
                    setPhases(updatedPhases);
                    setPhasesValid(updatedPhasesValid);
                  }}
                />
              ))} 

              <Grid container spacing={0} className="dialoguClass">
                <Grid item xs={11} sm ={11} md={11}>
                  <Grid container style={{justifyContent: "flex-end"}}>
                    <Button
                      type="button"
                      className="add-phase-button"
                      onClick={handlePushNewPhase}
                    >
                      <AddCircleOutline />
                      <span>Add Another Phase</span>
                    </Button>            
                  </Grid>
                </Grid>
              </Grid>
            </div>
          </Grid>
        </Grid>

        <Grid container spacing={2} className="dialoguClass">
          <Grid item xs={12} sm={12} md={10}>
            <div>Comments</div>
            <div>
              <textarea
                rows="3"
                type="text"
                className="form-control mt-3"
                maxLength="5000"
                placeholder="Enter your Comments"
                value={comment}
                onChange={e => {
                  const value = e.target.value;
                  setComment(value);
                  setCommentLength(value.length);
                }}
              ></textarea>
              <span className="character-count">{`${commentLength}/5000`}</span>
            </div>
          </Grid>
        </Grid>

        <Grid container spacing={2} className="dialoguClass">
          <Grid item xs={12} sm ={12} md={10}>
            <Grid container justifyContent="flex-end">
              <Button
                type="submit"
                className={"btn-submit" + ((!allValid || !phases.length) ? " btn-invalid" : "")}
              >
                Next
              </Button>            
            </Grid>
          </Grid>
        </Grid>
      </form>

      {/* Street Address Selection Modal */}
      <StreetAddressModal
        show={showAddressModal}
        onHide={handleCloseAddressModal}
        onAddressSelect={handleAddressSelect}
        phaseCity={(phases[activePhaseIndex] || projectInfo)?.city}
        phaseState={(phases[activePhaseIndex] || projectInfo)?.state}
        phaseZip={(phases[activePhaseIndex] || projectInfo)?.zip5}
        phaseStreet={(phases[activePhaseIndex] || projectInfo)?.street}
        phaseIndex={activePhaseIndex}
      />
    </Grid>
  );
};

export default RequestNewPhasePage;